import { forwardRef, useCallback, useEffect, useState, type HTMLAttributes } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti Carousel
 */
export type CarouselVariant = 'default' | 'autoplay' | 'fade' | 'thumbnails' | 'vertical';

/**
 * Carousel Item
 */
export interface CarouselItem {
  id: string | number;
  content: React.ReactNode;
  thumbnail?: string;
}

export interface CarouselProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'>, BaseComponentProps {
  /**
   * Variante carousel
   * @default 'default'
   */
  variant?: CarouselVariant;
  /**
   * Items da mostrare
   */
  items: CarouselItem[];
  /**
   * Autoplay (ms)
   */
  autoplayDelay?: number;
  /**
   * Loop infinito
   */
  loop?: boolean;
  /**
   * Mostra dots
   */
  dots?: boolean;
  /**
   * Mostra frecce
   */
  arrows?: boolean;
  /**
   * Slides da mostrare contemporaneamente
   */
  slidesToShow?: number;
  /**
   * Spacing tra slides
   */
  spacing?: 'none' | 'sm' | 'md' | 'lg';
}

/**
 * Carousel Component
 * 
 * Carousel potente basato su Embla con 5 varianti.
 * 
 * @example
 * ```tsx
 * <Carousel
 *   variant="default"
 *   items={[
 *     { id: 1, content: <div>Slide 1</div> },
 *     { id: 2, content: <div>Slide 2</div> }
 *   ]}
 *   arrows
 *   dots
 * />
 * ```
 */
export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      className,
      variant = 'default',
      items,
      autoplayDelay = 3000,
      loop = true,
      dots = true,
      arrows = true,
      slidesToShow = 1,
      spacing = 'md',
      ...props
    },
    ref
  ) => {
    const spacingClasses = {
      none: 'gap-0',
      sm: 'gap-2',
      md: 'gap-4',
      lg: 'gap-6',
    };

    const options: EmblaOptionsType = {
      loop,
      slidesToScroll: 1,
      align: 'start',
      ...(variant === 'vertical' && { axis: 'y' }),
    };

    const [emblaRef, emblaApi] = useEmblaCarousel(options);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const scrollPrev = useCallback(() => {
      if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    const scrollTo = useCallback(
      (index: number) => {
        if (emblaApi) emblaApi.scrollTo(index);
      },
      [emblaApi]
    );

    const onSelect = useCallback(() => {
      if (!emblaApi) return;
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
      if (!emblaApi) return;
      setScrollSnaps(emblaApi.scrollSnapList());
      emblaApi.on('select', onSelect);
      onSelect();

      return () => {
        emblaApi.off('select', onSelect);
      };
    }, [emblaApi, onSelect]);

    // Autoplay
    useEffect(() => {
      if (variant === 'autoplay' && emblaApi) {
        const autoplay = setInterval(() => {
          emblaApi.scrollNext();
        }, autoplayDelay);

        return () => clearInterval(autoplay);
      }
    }, [variant, emblaApi, autoplayDelay]);

    return (
      <div
        ref={ref}
        className={cn('relative', className)}
        {...props}
      >
        {/* Carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className={cn('flex', spacingClasses[spacing], variant === 'vertical' && 'flex-col')}>
            {items.map((item, index) => (
              <div
                key={item.id}
                className={cn(
                  'flex-shrink-0',
                  variant === 'fade' && 'relative',
                  variant === 'fade' && index !== selectedIndex && 'opacity-0',
                  `flex-[0_0_${100 / slidesToShow}%]`
                )}
              >
                {item.content}
              </div>
            ))}
          </div>
        </div>

        {/* Arrows */}
        {arrows && variant !== 'vertical' && (
          <>
            <button
              onClick={scrollPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-base-100/80 hover:bg-base-100 shadow-lg flex items-center justify-center transition-all z-10"
              aria-label="Previous"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={scrollNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-base-100/80 hover:bg-base-100 shadow-lg flex items-center justify-center transition-all z-10"
              aria-label="Next"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Dots */}
        {dots && (
          <div className="flex justify-center gap-2 mt-4">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={cn(
                  'w-2 h-2 rounded-full transition-all',
                  index === selectedIndex ? 'bg-primary w-8' : 'bg-base-300 hover:bg-base-content/40'
                )}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Thumbnails */}
        {variant === 'thumbnails' && (
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {items.map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollTo(index)}
                className={cn(
                  'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all',
                  index === selectedIndex ? 'border-primary' : 'border-base-300 opacity-60 hover:opacity-100'
                )}
              >
                {item.thumbnail ? (
                  <img src={item.thumbnail} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-base-200 flex items-center justify-center text-xs">
                    {index + 1}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

Carousel.displayName = 'Carousel';

